// Guitar Theory & Data for Melodalis Kawaii Guitar Academy

export interface GuitarStringInfo {
  stringNumber: number; // 1 to 6 (1 is high E, 6 is low E)
  name: string;
  baseNote: string;
  baseOctave: number;
  baseFreq: number;
}

export const GUITAR_STRINGS: GuitarStringInfo[] = [
  { stringNumber: 1, name: '1st (High E)', baseNote: 'E', baseOctave: 4, baseFreq: 329.63 },
  { stringNumber: 2, name: '2nd (B)', baseNote: 'B', baseOctave: 3, baseFreq: 246.94 },
  { stringNumber: 3, name: '3rd (G)', baseNote: 'G', baseOctave: 3, baseFreq: 196.00 },
  { stringNumber: 4, name: '4th (D)', baseNote: 'D', baseOctave: 3, baseFreq: 146.83 },
  { stringNumber: 5, name: '5th (A)', baseNote: 'A', baseOctave: 2, baseFreq: 110.00 },
  { stringNumber: 6, name: '6th (Low E)', baseNote: 'E', baseOctave: 2, baseFreq: 82.41 },
];

export const CHROMATIC_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export function getNoteAtFret(stringIndex: number, fret: number): { note: string; freq: number; isNatural: boolean } {
  const str = GUITAR_STRINGS[stringIndex];
  const freq = str.baseFreq * Math.pow(2, fret / 12);
  const startIdx = CHROMATIC_NOTES.indexOf(str.baseNote);
  const noteIdx = (startIdx + fret) % 12;
  const note = CHROMATIC_NOTES[noteIdx];
  const isNatural = !note.includes('#');
  return { note, freq, isNatural };
}

export interface ChordData {
  id: string;
  name: string;
  symbol: string;
  category: 'Major' | 'Minor' | '7th';
  difficulty: 'Super Easy 🐣' | 'Easy 🌸' | 'Medium ⭐';
  description: string;
  kawaiiVibe: string;
  // Frets for strings [1st (High E), 2nd (B), 3rd (G), 4th (D), 5th (A), 6th (Low E)]
  // -1 means Mute (X), 0 means Open, >0 is fret number
  frets: [number, number, number, number, number, number];
  // Fingers: 1=Index, 2=Middle, 3=Ring, 4=Pinky, 0=None
  fingers: [number, number, number, number, number, number];
  tips: string;
}

export const GUITAR_CHORDS: ChordData[] = [
  {
    id: 'c-major',
    name: 'C Major',
    symbol: 'C',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Bright, happy, and one of the most loved chords in pop and kawaii songs!',
    kawaiiVibe: 'Sunshine & rainbow drops 🌈',
    frets: [0, 1, 0, 2, 3, -1], // High E -> Low E: E(0), C(1), G(0), E(2), C(3), X
    fingers: [0, 1, 0, 2, 3, 0],
    tips: 'Curl your fingers like little cat paws so you don’t mute open strings!'
  },
  {
    id: 'g-major',
    name: 'G Major',
    symbol: 'G',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Rich, full, and energetic! Rings across all 6 strings beautifully.',
    kawaiiVibe: 'Warm morning breeze 🌻',
    frets: [3, 0, 0, 0, 2, 3],
    fingers: [3, 0, 0, 0, 1, 2],
    tips: 'Reach your middle finger high on the 6th string, and let strings 2, 3, 4 ring freely!'
  },
  {
    id: 'd-major',
    name: 'D Major',
    symbol: 'D',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Crisp and chimey like tiny fairy bells! Forms a cute little triangle shape.',
    kawaiiVibe: 'Sparkling fairy lights ✨',
    frets: [2, 3, 2, 0, -1, -1],
    fingers: [2, 3, 1, 0, 0, 0],
    tips: 'Look at the triangle shape between frets 2 and 3! Only strum the top 4 strings.'
  },
  {
    id: 'e-minor',
    name: 'E Minor',
    symbol: 'Em',
    category: 'Minor',
    difficulty: 'Super Easy 🐣',
    description: 'Deep, emotional, and only needs TWO fingers! The best chord to learn first.',
    kawaiiVibe: 'Cozy rainy day with hot cocoa ☕',
    frets: [0, 0, 0, 2, 2, 0],
    fingers: [0, 0, 0, 2, 1, 0],
    tips: 'Only two fingers on the 2nd fret! Press right behind the fret wire.'
  },
  {
    id: 'a-minor',
    name: 'A Minor',
    symbol: 'Am',
    category: 'Minor',
    difficulty: 'Easy 🌸',
    description: 'Sweet and melancholic, perfect for heartfelt acoustic ballads and pop intros.',
    kawaiiVibe: 'Gentle starry night 🌙',
    frets: [0, 1, 2, 2, 0, -1],
    fingers: [0, 1, 3, 2, 0, 0],
    tips: 'It looks just like E Major, just moved down one string! Magic!'
  },
  {
    id: 'a-major',
    name: 'A Major',
    symbol: 'A',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Three little finger buddies squished happily on the same 2nd fret!',
    kawaiiVibe: 'Happy picnic with friends 🥪',
    frets: [0, 2, 2, 2, 0, -1],
    fingers: [0, 3, 2, 1, 0, 0],
    tips: 'Stack your 3 fingers neatly inside fret 2. Strum from the open 5th string (A)!'
  },
  {
    id: 'e-major',
    name: 'E Major',
    symbol: 'E',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'A triumphant, powerful chord that vibrates the entire guitar body!',
    kawaiiVibe: 'Heroic magical girl transformation 💖',
    frets: [0, 0, 1, 2, 2, 0],
    fingers: [0, 0, 1, 3, 2, 0],
    tips: 'Hit all 6 strings with confidence! Feels like a burst of musical confetti.'
  },
  {
    id: 'd-minor',
    name: 'D Minor',
    symbol: 'Dm',
    category: 'Minor',
    difficulty: 'Medium ⭐',
    description: 'Dramatic and cinematic. Gives songs a magical, mysterious flavor.',
    kawaiiVibe: 'Secret enchanted forest 🍃',
    frets: [1, 3, 2, 0, -1, -1],
    fingers: [1, 3, 2, 0, 0, 0],
    tips: 'Stretch your pinky or ring finger to fret 3 on the B string while keeping fret 1 clean.'
  },
  {
    id: 'f-major-easy',
    name: 'F Major (Mini)',
    symbol: 'F',
    category: 'Major',
    difficulty: 'Medium ⭐',
    description: 'The friendly beginner version of the dreaded F chord! Sounds sweet & warm.',
    kawaiiVibe: 'Little pastel blossom 🌸',
    frets: [1, 1, 2, 3, -1, -1],
    fingers: [1, 1, 2, 3, 0, 0],
    tips: 'Barre just the first two strings with your index finger flat, not the whole neck!'
  },
  {
    id: 'c7',
    name: 'C Dominant 7th',
    symbol: 'C7',
    category: '7th',
    difficulty: 'Medium ⭐',
    description: 'A jazzy, groovy twist on C Major with an added pinky note.',
    kawaiiVibe: 'Bubbly retro cafe swing 🎷',
    frets: [0, 1, 3, 2, 3, -1],
    fingers: [0, 1, 4, 2, 3, 0],
    tips: 'Take your regular C Major shape and pop your pinky onto the 3rd fret of the G string!'
  }
];

export function getChordFrequencies(chord: ChordData): (number | null)[] {
  // frets: [highE, B, G, D, A, lowE]
  // In GUITAR_STRINGS: [0: High E, 1: B, 2: G, 3: D, 4: A, 5: Low E]
  // We return low to high for strumming: Low E -> High E
  const result: (number | null)[] = [];
  for (let s = 5; s >= 0; s--) {
    const fret = chord.frets[s];
    if (fret === -1) {
      result.push(null);
    } else {
      const str = GUITAR_STRINGS[s];
      result.push(str.baseFreq * Math.pow(2, fret / 12));
    }
  }
  return result;
}

